import React, { useEffect, useMemo, useRef, useState } from "react";

import { useWebsitePromotions } from "../../context/WebsitePromotionsContext";
import { useHomepageHydration } from "../../context/HomepageRenderContext";
import PromotionLink from "./PromotionLink";
import SmartImage from "../Image";
import { extractImageOptimizationMetadata } from "../../utils/imageVariants";
import { resolvePromotionYouTube } from "./promotionYouTube";

const resolveText = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const normalizePromotions = (items = []) =>
  (Array.isArray(items) ? items : []).filter(Boolean);

const DEFAULT_AUTOPLAY_INTERVAL_MS = 7000;
const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const resolvePageBannerMedia = (promotion = {}) => {
  const slots = promotion?.mediaSuite?.slots || {};
  return {
    desktop:
      slots?.desktopBanner?.url ||
      slots?.desktopHero?.url ||
      promotion?.media?.url ||
      "",
    mobile:
      slots?.mobileBanner?.url ||
      slots?.mobileHero?.url ||
      slots?.desktopBanner?.url ||
      slots?.desktopHero?.url ||
      promotion?.media?.url ||
      "",
    altText:
      promotion?.mediaSuite?.metadata?.altText ||
      promotion?.media?.altText ||
      resolveText(promotion?.title, promotion?.subtitle, "Website promotion"),
  };
};

const resolveHref = (promotion = {}) =>
  resolveText(
    promotion?.cta?.href,
    promotion?.target?.href,
    promotion?.targetSnapshot?.href,
  );

function ArrowIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M10 17l5-5-5-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PauseIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M9 7v10M15 7v10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PlayIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M9 7l8 5-8 5V7z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function WebsitePromotionPageBannerSlide({
  promotion,
  variant = "hero",
  alignWithPageContent = false,
  onClick,
}) {
  if (!promotion) return null;

  const title = resolveText(promotion?.title);
  const subtitle = resolveText(promotion?.subtitle, promotion?.description);
  const description = resolveText(promotion?.description);
  const ctaLabel = resolveText(promotion?.cta?.label, "Explore");
  const href = resolveHref(promotion);
  const media = resolvePageBannerMedia(promotion);
  const youtube = resolvePromotionYouTube(promotion);
  const optimization = extractImageOptimizationMetadata(promotion);
  const hasOptimizedDelivery = Boolean(optimization?.delivery);
  const isCompact = variant === "inline";
  const isPageContentAligned = alignWithPageContent && !isCompact;
  const hasOverlayCopy = !youtube && Boolean(title || subtitle || description);
  const bannerWidthClass = isCompact
    ? "max-w-6xl"
    : isPageContentAligned
      ? "max-w-[1376px]"
      : "max-w-[1360px]";

  if (!youtube && !media.desktop && !media.mobile && !hasOptimizedDelivery) return null;

  return (
    <div
      className={[
        "ecotour-page-banner group relative overflow-hidden border border-white/10 bg-slate-950 text-white shadow-[0_20px_54px_rgba(15,35,54,0.14)]",
        isCompact
          ? `ecotour-page-banner--inline mx-auto ${bannerWidthClass} rounded-[30px]`
          : `ecotour-page-banner--hero mx-auto w-full ${bannerWidthClass} overflow-hidden rounded-[28px] sm:rounded-[34px]`,
      ].join(" ")}
    >
      <div className="absolute inset-0">
        {youtube ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <iframe
              src={`${youtube.embedUrl}?autoplay=1&mute=1&playsinline=1&rel=0&modestbranding=1`}
              title={title || media.altText || "YouTube promotion video"}
              className="aspect-video max-h-full w-full border-0"
              allow="autoplay; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        ) : hasOptimizedDelivery ? (
          <SmartImage
            image={{
              url: media.desktop || media.mobile || optimization.delivery.fallbackUrl,
              metadata: { imageOptimization: optimization },
            }}
            fallback={media.desktop || media.mobile || optimization.delivery.fallbackUrl}
            surfaceKey={isCompact ? "promotion.inline" : "promotion.page-banner"}
            alt={media.altText}
            fill
            fit="contain"
            priority
            loading="eager"
            sizes="(max-width: 767px) 100vw, 1360px"
            className="h-full w-full"
          />
        ) : (
          <picture>
            {media.mobile ? <source media="(max-width: 767px)" srcSet={media.mobile} /> : null}
            <img
              src={media.desktop || media.mobile}
              alt={media.altText}
              width="1360"
              height="560"
              className="absolute inset-0 h-full w-full max-w-none object-contain object-center"
              loading="eager"
            />
          </picture>
        )}
        {!youtube ? (
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/12 via-black/0 to-black/0" />
        ) : null}
      </div>

      {href && !youtube && !hasOverlayCopy ? (
        <PromotionLink
          href={href}
          isExternal={promotion?.cta?.isExternal || promotion?.target?.isExternal}
          openMode={promotion?.cta?.openMode || promotion?.target?.openMode}
          aria-label={`${ctaLabel}: ${media.altText}`}
          className="ecotour-page-banner__media-link absolute inset-0 z-10 focus:outline-none focus:ring-4 focus:ring-emerald-300/70 focus:ring-inset"
          onClick={onClick}
        >
          <span className="sr-only">{ctaLabel}</span>
        </PromotionLink>
      ) : null}

      {hasOverlayCopy ? (
        <div
          className={[
            "ecotour-page-banner__content absolute inset-0 z-10 flex items-end",
            isCompact
              ? "px-5 py-7 sm:px-7 sm:py-8"
              : "px-5 py-6 sm:px-9 sm:py-9 lg:px-14 lg:py-12",
          ].join(" ")}
        >
          <div className="w-full max-w-none">
            {title ? (
              <h2
                className={[
                  "max-w-[96%] font-bold leading-[0.96] tracking-[-0.04em] text-white text-balance",
                  isCompact
                    ? "text-[2.15rem] sm:text-[3rem]"
                    : "text-[2.3rem] sm:text-[3.9rem] lg:text-[4.9rem] xl:text-[5.8rem]",
                ].join(" ")}
              >
                {title}
              </h2>
            ) : null}

            {subtitle ? (
              <p className={`max-w-[96%] text-sm font-semibold leading-relaxed text-white sm:text-base ${title ? "mt-4" : ""}`}>
                {subtitle}
              </p>
            ) : null}

            {description && description !== subtitle ? (
              <p className={`max-w-[96%] text-xs font-semibold leading-relaxed text-white sm:text-sm ${subtitle || title ? "mt-3" : ""}`}>
                {description}
              </p>
            ) : null}

            {href ? (
              <PromotionLink
                href={href}
                isExternal={promotion?.cta?.isExternal || promotion?.target?.isExternal}
                openMode={promotion?.cta?.openMode || promotion?.target?.openMode}
                className="ecotour-page-banner__cta group mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-white px-3 py-2 text-xs font-semibold text-slate-950 shadow-lg shadow-black/20 transition hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 focus:ring-offset-slate-950/30 sm:px-5 sm:py-3 sm:text-sm"
                onClick={onClick}
              >
                {ctaLabel}
                <span className="transition-transform duration-200 group-hover:translate-x-0.5 motion-reduce:transition-none">
                  <ArrowIcon className="h-4 w-4" />
                </span>
              </PromotionLink>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function WebsitePromotionPageBanner({
  promotions,
  variant = "hero",
  className = "",
  alignWithPageContent = false,
  autoPlay = true,
  autoPlayIntervalMs = DEFAULT_AUTOPLAY_INTERVAL_MS,
  onPromotionClick,
  onPromotionImpression,
}) {
  const hydrating = useHomepageHydration();
  const slides = useMemo(() => normalizePromotions(promotions), [promotions]);
  const multi = slides.length > 1;
  const isCompact = variant === "inline";
  const isPageContentAligned = alignWithPageContent && !isCompact;
  const controlsWidthClass = isCompact
    ? "max-w-6xl"
    : isPageContentAligned
      ? "max-w-[1376px]"
      : "max-w-[1360px]";
  const viewportRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlayPaused, setIsAutoPlayPaused] = useState(() => hydrating ? false : prefersReducedMotion());
  const [isInViewport, setIsInViewport] = useState(true);
  const [isDocumentVisible, setIsDocumentVisible] = useState(
    () => typeof document === "undefined" || document.visibilityState !== "hidden",
  );
  const resolvedAutoPlayIntervalMs = Math.max(
    Number(autoPlayIntervalMs) || DEFAULT_AUTOPLAY_INTERVAL_MS,
    5000,
  );

  useEffect(() => {
    setActiveIndex((current) => (current >= slides.length ? 0 : current));
  }, [slides.length]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handleChange = (event) => {
      if (event.matches) setIsAutoPlayPaused(true);
    };
    if (media.matches) setIsAutoPlayPaused(true);
    media.addEventListener?.("change", handleChange);
    return () => media.removeEventListener?.("change", handleChange);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const handleVisibilityChange = () => {
      setIsDocumentVisible(document.visibilityState !== "hidden");
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  useEffect(() => {
    const node = viewportRef.current;
    if (!multi || !node || typeof IntersectionObserver === "undefined") {
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInViewport(Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.3));
      },
      { threshold: [0, 0.3] },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [multi]);

  useEffect(() => {
    if (!multi || typeof window === "undefined" || typeof window.Image !== "function") {
      return undefined;
    }
    const nextPromotion = slides[(activeIndex + 1) % slides.length];
    const nextYouTube = resolvePromotionYouTube(nextPromotion);
    const nextMedia = resolvePageBannerMedia(nextPromotion);
    const useMobile = window.matchMedia?.("(max-width: 767px)")?.matches;
    const nextUrl = nextYouTube?.thumbnailUrl ||
      (useMobile
        ? nextMedia.mobile || nextMedia.desktop
        : nextMedia.desktop || nextMedia.mobile);
    if (!nextUrl) return undefined;
    const image = new window.Image();
    image.decoding = "async";
    image.src = nextUrl;
    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, [activeIndex, multi, slides]);

  useEffect(() => {
    if (
      !autoPlay ||
      !multi ||
      isAutoPlayPaused ||
      !isInViewport ||
      !isDocumentVisible
    ) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setActiveIndex((current) => (current + 1) % slides.length);
    }, resolvedAutoPlayIntervalMs);
    return () => window.clearTimeout(timer);
  }, [
    activeIndex,
    autoPlay,
    isAutoPlayPaused,
    isDocumentVisible,
    isInViewport,
    multi,
    resolvedAutoPlayIntervalMs,
    slides.length,
  ]);

  const activePromotion = slides[activeIndex] || null;
  const activeYouTube = resolvePromotionYouTube(activePromotion);
  const activeHref = resolveHref(activePromotion);
  const activeCtaLabel = resolveText(activePromotion?.cta?.label, "Explore");

  useEffect(() => {
    if (!activePromotion || !onPromotionImpression) return;
    onPromotionImpression(activePromotion, {
      placementSurface: "page-banner",
      variant,
      slideIndex: activeIndex,
      totalSlides: slides.length,
    });
  }, [activeIndex, activePromotion, onPromotionImpression, slides.length, variant]);

  if (!slides.length) return null;

  const goTo = (index) => {
    if (!slides.length) return;
    const nextIndex = ((index % slides.length) + slides.length) % slides.length;
    setIsAutoPlayPaused(true);
    setActiveIndex(nextIndex);
  };

  const prev = () => goTo(activeIndex - 1);
  const next = () => goTo(activeIndex + 1);

  return (
    <section
      className={className}
      aria-label="Featured promotions"
      aria-roledescription={multi ? "carousel" : undefined}
    >
      <div
        ref={viewportRef}
        className={[
          "ecotour-page-banner__viewport relative",
          isPageContentAligned ? "is-page-content-aligned" : "px-3 sm:px-5 lg:px-7",
        ].join(" ")}
      >
        <WebsitePromotionPageBannerSlide
          key={activePromotion?.id || activePromotion?._id || activeIndex}
          promotion={activePromotion}
          variant={variant}
          alignWithPageContent={isPageContentAligned}
          onClick={() =>
            onPromotionClick?.(activePromotion, {
              placementSurface: "page-banner",
              variant,
              slideIndex: activeIndex,
              totalSlides: slides.length,
            })
          }
        />

        {multi || (activeYouTube && activeHref) ? (
          <div
            className={`ecotour-page-banner__controls mx-auto mt-2 flex w-full ${controlsWidthClass} flex-wrap items-center justify-center gap-2 sm:mt-3 md:absolute md:bottom-4 md:left-1/2 md:z-30 md:mt-0 md:-translate-x-1/2 md:justify-end md:px-4 lg:bottom-5 lg:px-5`}
          >
            {multi ? (
              <div className="ecotour-page-banner__glass-controls inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white/95 p-1.5 text-slate-700 shadow-[0_8px_24px_rgba(15,23,42,0.12)] backdrop-blur md:border-white/25 md:bg-slate-950/30 md:text-white md:shadow-[0_14px_36px_rgba(2,8,23,0.28)] md:backdrop-blur-xl md:ring-1 md:ring-white/10">
                <button
                  type="button"
                  onClick={prev}
                  aria-label="Previous promotion"
                  className="ecotour-page-banner__nav inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-700 transition hover:bg-emerald-50 hover:text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 md:text-white md:hover:bg-white/15 md:hover:text-white md:focus:ring-white/60"
                >
                  <ArrowIcon className="h-5 w-5 rotate-180" />
                </button>

                <div className="ecotour-page-banner__pagination flex items-center gap-2 px-1">
                  {slides.map((promotion, index) => {
                    const active = index === activeIndex;
                    return (
                      <button
                        key={`promotion-dot-${promotion?.id || promotion?._id || index}`}
                        type="button"
                        onClick={() => goTo(index)}
                        aria-label={`Go to promotion ${index + 1}`}
                        aria-current={active ? "true" : "false"}
                        className={[
                          "ecotour-page-banner__dot h-2 rounded-full transition-all duration-300 motion-reduce:transition-none focus:outline-none focus:ring-2 focus:ring-emerald-500/40",
                          active
                            ? "is-active w-8 bg-emerald-700 md:bg-white"
                            : "w-2 bg-slate-300 hover:bg-emerald-300 md:bg-white/40 md:hover:bg-white/70",
                        ].join(" ")}
                      />
                    );
                  })}
                </div>

                {autoPlay ? (
                  <button
                    type="button"
                    onClick={() => setIsAutoPlayPaused((current) => !current)}
                    aria-label={isAutoPlayPaused ? "Play promotions" : "Pause promotions"}
                    aria-pressed={isAutoPlayPaused}
                    className="ecotour-page-banner__autoplay inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-700 transition hover:bg-emerald-50 hover:text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 md:text-white md:hover:bg-white/15 md:hover:text-white md:focus:ring-white/60"
                  >
                    {isAutoPlayPaused ? (
                      <PlayIcon className="h-4 w-4" />
                    ) : (
                      <PauseIcon className="h-4 w-4" />
                    )}
                  </button>
                ) : null}

                <button
                  type="button"
                  onClick={next}
                  aria-label="Next promotion"
                  className="ecotour-page-banner__nav inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-700 transition hover:bg-emerald-50 hover:text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 md:text-white md:hover:bg-white/15 md:hover:text-white md:focus:ring-white/60"
                >
                  <ArrowIcon className="h-5 w-5" />
                </button>
              </div>
            ) : null}

            {activeYouTube && activeHref ? (
              <PromotionLink
                href={activeHref}
                isExternal={activePromotion?.cta?.isExternal || activePromotion?.target?.isExternal}
                openMode={activePromotion?.cta?.openMode || activePromotion?.target?.openMode}
                className="ecotour-page-banner__cta inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-emerald-800 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(15,23,42,0.14)] transition hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 md:border md:border-white/30 md:bg-white/90 md:text-slate-950 md:backdrop-blur-xl md:hover:bg-white md:focus:ring-white/60"
                onClick={() =>
                  onPromotionClick?.(activePromotion, {
                    placementSurface: "page-banner",
                    variant,
                    slideIndex: activeIndex,
                    totalSlides: slides.length,
                  })
                }
              >
                {activeCtaLabel}
                <ArrowIcon className="h-4 w-4" />
              </PromotionLink>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function WebsitePromotionPageBannerSlot({
  variant = "hero",
  className = "",
  alignWithPageContent = false,
  autoPlay = true,
  autoPlayIntervalMs = DEFAULT_AUTOPLAY_INTERVAL_MS,
}) {
  const { pagePath } = useWebsitePromotions();
  return <StablePageBannerSlot key={pagePath} {...{ variant, className, alignWithPageContent, autoPlay, autoPlayIntervalMs }} />;
}

function StablePageBannerSlot({ variant, className, alignWithPageContent, autoPlay, autoPlayIntervalMs }) {
  const {
    pageBannerPromotions,
    recordPromotionImpression,
    trackPromotionClick,
  } = useWebsitePromotions();
  // Decide top-of-page occupancy before paint. If bootstrap is unavailable or
  // empty, late API responses cannot push an already visible catalogue down.
  // A route change creates a fresh decision; other promotion placements retain
  // their normal asynchronous delivery. No speculative blank banner is reserved.
  const [admitted] = useState(() => variant === "inline" || Boolean(pageBannerPromotions?.length));
  if (!admitted || !pageBannerPromotions?.length) return null;

  return (
    <div className={`ecotour-page-banner-reveal ${variant === "inline" ? "ecotour-page-banner-reveal--inline" : "ecotour-page-banner-reveal--stable"}`}>
      <div className="ecotour-page-banner-reveal__inner">
        <WebsitePromotionPageBanner
          promotions={pageBannerPromotions}
          variant={variant}
          className={className}
          alignWithPageContent={alignWithPageContent}
          autoPlay={autoPlay}
          autoPlayIntervalMs={autoPlayIntervalMs}
          onPromotionImpression={recordPromotionImpression}
          onPromotionClick={(promotion, metadata) => trackPromotionClick(promotion, metadata)}
        />
      </div>
    </div>
  );
}

export default WebsitePromotionPageBanner;
